import React, { useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { TFunc } from '../../../../../../../hooks/useNamespacedTranslation';
import {
  FILE_SIZE_LIMIT,
  collectFiles,
  fileSizeFormatter,
  sumFileSizes,
} from '../../../../../../../utils/file';
import { useToasts } from '../../../../../../../context/Toast';
import FlairIcon from '../../../../../../../atomic/atoms/FlairIcon';
import classNames from 'classnames';

export type FileData = {
  fileName: string;
  fileBase64: string;
  size: number;
};

type Props = {
  t: TFunc;
  onFilesChange: (files: FileData[]) => void;
  files: FileData[];
};

export const FileUploadFormField: React.FC<React.PropsWithChildren<Props>> = ({
  t,
  onFilesChange,
  files,
}) => {
  const { addError } = useToasts();

  // Base64 encoding is async, so the `files` captured when a pick starts can be
  // out of date by the time it finishes — the user may have removed a file or
  // picked another batch meanwhile. Append onto the latest committed list so
  // neither write is lost.
  const latestFiles = useRef(files);
  useEffect(() => {
    latestFiles.current = files;
  }, [files]);

  const onFileChange = async ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    if (!target.validity.valid || !target.files) {
      return;
    }

    const picked = Array.from(target.files);
    // Clear the input so removing a file, or re-picking the same selection,
    // still fires a change event.
    target.value = '';

    // Checked against the raw File sizes so an over-budget batch is rejected
    // without paying to base64-encode it first.
    if (sumFileSizes([...latestFiles.current, ...picked]) > FILE_SIZE_LIMIT) {
      addError(
        t('form.fields.fileSizeLimitError', {
          size: fileSizeFormatter(FILE_SIZE_LIMIT),
        }),
      );
      return;
    }

    const collected = await collectFiles(picked);
    const appended = [...latestFiles.current, ...collected];
    latestFiles.current = appended;
    onFilesChange(appended);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = latestFiles.current.filter((_, i) => i !== index);
    latestFiles.current = updatedFiles;
    onFilesChange(updatedFiles);
  };

  return (
    <div>
      <Form.Group>
        <Form.Label htmlFor="ticketFile">
          {t('form.fields.fileLabel')}
          <span className="text-muted ms-2 small">
            ({t('form.fields.fileOptional')})
          </span>
        </Form.Label>
        <div id="ticketFileSizeHint" className="small text-muted mb-2">
          {t('form.fields.fileSizeHint', {
            size: fileSizeFormatter(FILE_SIZE_LIMIT),
          })}
        </div>
        <Form.Control
          id="ticketFile"
          type="file"
          multiple
          aria-describedby="ticketFileSizeHint"
          onChange={onFileChange}
        />
      </Form.Group>

      {files.length > 0 && (
        <div className="mt-3">
          {files.map((file, index) => (
            <div
              key={index}
              className={classNames(
                'd-flex',
                'align-items-center',
                'justify-content-between',
                'py-2',
                'gap-3',
              )}>
              <div className="d-flex align-items-center gap-2">
                <FlairIcon
                  icon="document-outline"
                  size="sm"
                  className="text-muted"
                />
                <span>{file.fileName}</span>
                <span className="text-muted small">
                  ({fileSizeFormatter(file.size)})
                </span>
              </div>
              <FlairIcon
                icon="trash-outline"
                className="text-dark align-middle"
                role="button"
                onClick={() => handleRemoveFile(index)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
